import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  publicApi,
  REPORT_CLASSIFICATIONS,
  REPORT_COMMENT_MAX,
  type ReportClassification,
} from '@/lib/api';
import { friendlyError } from '@/lib/errors';

/**
 * ReportProfileModal — submitted from the public /p/:slug page.
 *
 * UX rules:
 *  - Backdrop click + Escape close.
 *  - Classification radio list (mandatory pick); free-form comment
 *    textarea is ALWAYS enabled regardless of selection, with a small
 *    char counter. "Other" is just one of the radios.
 *  - Success state collapses the form into a "thanks, we'll take a
 *    look" panel — intentionally vague so the same UI shape works
 *    whether or not the report is novel (no information leak on
 *    duplicate submissions).
 *
 * Auth: optional. Signed-in callers are recorded; anonymous reports
 * are allowed (the page itself is signed-out-viewable). The backend
 * rate-limits to N/day per IP.
 */
export function ReportProfileModal({
  open,
  slug,
  onClose,
}: {
  open: boolean;
  slug: string;
  onClose: () => void;
}) {
  const [classification, setClassification] = useState<ReportClassification | null>(null);
  const [comment, setComment] = useState('');
  const [state, setState] = useState<'idle' | 'submitting' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);

  // Reset every time the modal re-opens so re-reports start clean.
  useEffect(() => {
    if (open) {
      setClassification(null);
      setComment('');
      setState('idle');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const canSubmit = classification !== null && state !== 'submitting';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || classification === null) return;
    setState('submitting');
    setError(null);
    try {
      await publicApi.report(slug, {
        classification,
        comment: comment.trim(),
      });
      setState('done');
    } catch (err) {
      setError(friendlyError(err, 'reportProfile'));
      setState('idle');
    }
  };

  return createPortal(
    <div style={backdrop} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button type="button" onClick={onClose} aria-label="Close" style={closeX}>×</button>

        {state === 'done' ? (
          <>
            <div className="mono" style={eyebrow}>RECEIVED</div>
            <h2 className="serif" style={title}>Thanks for telling us.</h2>
            <p style={body}>
              We&rsquo;ll take a look. Reports help keep WYDM honest.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button type="button" onClick={onClose} style={primaryBtn}>Close</button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <div className="mono" style={eyebrow}>REPORT</div>
            <h2 className="serif" style={title}>What&rsquo;s the issue?</h2>
            <p style={body}>
              Pick one + add details if it helps. Reports are reviewed by humans.
            </p>

            <div role="radiogroup" aria-label="Reason" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
              {REPORT_CLASSIFICATIONS.map((c) => {
                const active = classification === c.id;
                return (
                  <label
                    key={c.id}
                    style={{
                      display: 'flex',
                      gap: 12,
                      alignItems: 'flex-start',
                      padding: '11px 14px',
                      borderRadius: 12,
                      border: '1px solid',
                      borderColor: active ? 'var(--coral)' : 'var(--line)',
                      background: active ? 'rgba(232,154,110,0.08)' : 'var(--bg)',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name="report-classification"
                      value={c.id}
                      checked={active}
                      onChange={() => setClassification(c.id)}
                      style={{ marginTop: 3, accentColor: 'var(--coral)' }}
                    />
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{c.label}</span>
                      {c.hint && (
                        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{c.hint}</span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>

            <label style={{ display: 'block', fontSize: 11, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }} className="mono">
              More details (optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, REPORT_COMMENT_MAX))}
              rows={4}
              placeholder="What did you see? Where on the profile?"
              style={textarea}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, marginBottom: 18 }}>
              {error
                ? <span style={errorLine}>{error}</span>
                : <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>Helps us act on it faster.</span>}
              <span style={{ color: comment.length >= REPORT_COMMENT_MAX ? 'var(--coral)' : 'var(--ink-3)', fontSize: 11 }} className="mono">
                {comment.length}/{REPORT_COMMENT_MAX}
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={onClose} style={cancelBtn}>Cancel</button>
              <button type="submit" disabled={!canSubmit} style={{ ...primaryBtn, opacity: canSubmit ? 1 : 0.45, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
                {state === 'submitting' ? 'Submitting…' : 'Submit report'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(43, 29, 20, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
  padding: 16,
};

const card: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: 22,
  padding: '28px 26px 22px',
  width: '100%',
  maxWidth: 480,
  boxShadow: '0 28px 64px rgba(0,0,0,0.32)',
  position: 'relative',
  maxHeight: 'calc(100vh - 32px)',
  overflowY: 'auto',
};

const closeX: React.CSSProperties = {
  position: 'absolute',
  top: 12,
  right: 12,
  width: 32,
  height: 32,
  borderRadius: '50%',
  border: 'none',
  background: 'transparent',
  color: 'var(--ink-3)',
  fontSize: 22,
  cursor: 'pointer',
  lineHeight: 1,
};

const eyebrow: React.CSSProperties = {
  fontSize: 11,
  letterSpacing: 2,
  color: 'var(--coral)',
  textTransform: 'uppercase',
  marginBottom: 6,
};

const title: React.CSSProperties = {
  fontSize: 26,
  fontWeight: 500,
  fontStyle: 'italic',
  margin: '0 0 8px',
  color: 'var(--ink)',
};

const body: React.CSSProperties = {
  fontSize: 13,
  lineHeight: 1.5,
  color: 'var(--ink-2)',
  margin: '0 0 18px',
};

const textarea: React.CSSProperties = {
  width: '100%',
  background: 'var(--bg)',
  border: '1px solid var(--line)',
  borderRadius: 12,
  padding: '11px 14px',
  fontSize: 13,
  fontFamily: 'var(--font-ui)',
  outline: 'none',
  color: 'var(--ink)',
  resize: 'vertical',
  minHeight: 80,
  lineHeight: 1.5,
};

const errorLine: React.CSSProperties = {
  fontSize: 12,
  color: '#a8331f',
};

const cancelBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--line)',
  color: 'var(--ink-3)',
  borderRadius: 100,
  padding: '9px 16px',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)',
};

const primaryBtn: React.CSSProperties = {
  background: 'var(--coral)',
  color: '#fff',
  border: 'none',
  borderRadius: 100,
  padding: '9px 18px',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)',
};
